import { useState, useEffect, useRef, useCallback } from "react";
import { checkActivationStatus, getActivationCode, handleActivationResult, checkPollingTimeout, storeAccessToken } from "../utils/activation";

export function useActivationFlow(onActivationComplete, activationConfig) {
    const { isMultiStep } = activationConfig;
    const initialStep = isMultiStep ? 1 : 2;
    const [step, setStep] = useState(initialStep);
    const [code, setCode] = useState(null);
    const intervalRef = useRef(null);
    const pollingStartTimeRef = useRef(null);

    // Stops the activation polling by clearing the interval
    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    // Manage polling start condition
    const startPolling = useCallback(() => {
        const isAlreadyPolling = !!intervalRef.current;
        const hasActivationCode = !!code;

        // Do not start if already polling or no code available
        if (isAlreadyPolling || !hasActivationCode) {
            return;
        }

        // Record when polling started (for 10 min timeout calculation)
        pollingStartTimeRef.current = Date.now();

        // Poll every 5 seconds
        intervalRef.current = setInterval(pollActivation, 5000);
    }, [code]);

    // Perform the polling action
    const pollActivation = async () => {
        const hasTimedOut = checkPollingTimeout(pollingStartTimeRef, setStep, stopPolling, isMultiStep);
        if (hasTimedOut) {
            return;
        }

        const activationResult = await checkActivationStatus(code);

        // Process the result (invoke StateService)
        handleActivationResult(activationResult, { stopPolling, onActivationComplete });
    };

    // Clean up polling interval when component unmounts
    useEffect(() => {
        return () => stopPolling();
    }, [stopPolling]);

    // Fetch activation code only when user reaches step 2
    useEffect(() => {
        const shouldFetchCode = step === 2 && !code;

        if (shouldFetchCode) {
            async function fetchActivationCodeAndTokenFromServer() {
                const result =  await getActivationCode();
                if(result?.token){
                    storeAccessToken(result.token);
                    onActivationComplete(result.token);
                }else if (result?.code) {
                    setCode(result.code);
                }
            }
            fetchActivationCodeAndTokenFromServer();
        }
    }, [step, code, onActivationComplete]);

    // Start/stop polling based on current step and code availability
    useEffect(() => {
        const isOnActivationScreen = step === 2;
        const hasActivationCode = !!code;
        const isCurrentlyPolling = !!intervalRef.current;

        // Start polling when on step 2, have code, and not already polling
        const shouldStartPolling = isOnActivationScreen && hasActivationCode && !isCurrentlyPolling;

        // Stop polling when not on step 2 but currently polling
        const shouldStopPolling = !isOnActivationScreen && isCurrentlyPolling;

        if (shouldStartPolling) {
            startPolling();
        } else if (shouldStopPolling) {
            stopPolling();
        }

    }, [step, code, startPolling, stopPolling]);

    return {
        step,
        setStep,
        code,
    };
}
